import Breadcrumbs from '../../components/common/breadcrumbs.tsx';
import { useApolloClient, useMutation, useSuspenseQuery } from '@apollo/client';
import { GET_QUIZ, QUIZ_FRAGMENT, UPDATE_QUIZ } from '../../apollo/quizzes.ts';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useFragment } from '../../apollo/__generated__';
import Button from '../../components/common/button.tsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import Select from '../../components/common/select.tsx';
import {
  OrderedQuestionsInput,
  QuizStatus,
  UserActivityLogType,
} from '../../apollo/__generated__/graphql.ts';
import { toast } from 'react-toastify';
import Modal from '../../components/common/modal.tsx';
import CreateQuizForm from './create-quiz-form.tsx';
import { shuffleArray } from '../../components/quizzes-browser/quizzes-utils.tsx';
import QuizQuestion from '../../components/quizzes-browser/quiz-question.tsx';
import { useRemoveQuizzes } from '../../components/quizzes-browser/quizzes-hooks.tsx';
import AddQuestionsModal from '../../components/questions/add-questions-modal.tsx';
import RemoveModal from '../../components/common/remove-modal.tsx';
import RemoveButton from '../../components/common/remove-button.tsx';
import { Popover } from '../../components/common/popover.tsx';
import {
  IQuestionsExportSettingsFormData,
  QuestionsExportSettingsForm,
} from '../../components/questions/questions-export-settings-form.tsx';
import ActionsDropdown from '../../components/table/actions-dropdown.tsx';
import { useGetMe } from '../../utils/hooks.ts';
import { cn, createPdfExportUrl } from '../../utils/common-utils.ts';
import { NavBarEnum } from '../../utils/enums.ts';
import { Fragment } from 'react';
import { Reorder } from 'framer-motion';
import QuizQuestionItem from './quizes/quiz-question-item.tsx';
import { isEqual } from 'lodash';
import QuestionPreview from '../../components/questions/question-content/question-preview.tsx';
import copyTextToClipboard from '../../utils/copy-text-to-clipboard.ts';
import { QuizQuestionsOrderEnum } from '../../enums/index.ts';
import ArrowIcon from '../../assets/icons/arrow-right.svg?react';
import CrossMarkIcon from '../../assets/icons/cross-mark.svg?react';
import { useCreateExportLog } from '../../hooks/use-create-export-log.ts';
import {
  ChevronDoubleDownIcon,
  ChevronDoubleUpIcon,
} from '@heroicons/react/24/outline';
import LocalDate from '../../components/common/local-date.tsx';

const QuizInformation = () => {
  const [isRemoveAllModal, setRemoveAllModal] = useState(false);
  const [isQuestionsBrowser, setQuestionsBrowser] = useState(false);
  const [isQuizEditModal, setQuizEditModal] = useState(false);
  const [selectedQuestions, setSelectedQuestion] = useState<number[]>([]);
  const [insertQuestionAtIndex, setInsertQuestionAtIndex] = useState<number | null>(null);
  const [questionToChange, setQuestionToChange] = useState<number | null>(null);
  const [previewQuestionId, setPreviewQuestionId] = useState<number>();
  const [isExpandedQuestions, setIsExpandedQuestions] = useState(false);
  const [expandedQuestionIds, setExpandedQuestionIds] = useState<Set<number>>(
    () => new Set(),
  );
  const [pendingInserts, setPendingInserts] = useState<Map<number, number>>(
    () => new Map(),
  );
  const quizQuestionsRef = useRef<NonNullable<typeof quiz>['quizQuestions']>([]);

  const { isAdmin, isSales, isTeacher } = useGetMe();
  const canEdit = !isSales;
  const canExport = !isSales;
  const client = useApolloClient();
  const navigate = useNavigate();
  const params = useParams();
  const slug = params?.slug;
  const [quizLookupId, setQuizLookupId] = useState<{
    routeSlug?: string;
    id: number;
  }>();
  const quizLookupInput =
    quizLookupId && quizLookupId.routeSlug === slug
      ? { id: quizLookupId.id }
      : { slug };

  const redirect = () => navigate('/quizzes-browser', { replace: true });
  const { onRemoveQuizzes, onCloseRemove, onOpenRemove, removeModal } =
    useRemoveQuizzes(redirect);
  const { data } = useSuspenseQuery(GET_QUIZ, {
    variables: {
      input: quizLookupInput,
    },
    errorPolicy: 'all',
  });
  const quiz = useFragment(QUIZ_FRAGMENT, data?.quiz?.getQuiz);

  const [quizQuestions, setQuizQuestions] = useState(quiz?.quizQuestions);
  const createExportLog = useCreateExportLog();

  useEffect(() => {}, [quizQuestions]);

  const [quizQuestionsOrder, setQuizQuestionsOrder] =
    useState<QuizQuestionsOrderEnum>(QuizQuestionsOrderEnum.NON);

  const quizQuestionToggle = () => {
    const map = Object.values(QuizQuestionsOrderEnum);

    const orderEnumIndex = map.indexOf(quizQuestionsOrder);

    const base = [...(quiz?.quizQuestions || [])];

    switch (map[orderEnumIndex + 1]) {
      case undefined: {
        setQuizQuestions([...base]);
        setQuizQuestionsOrder(QuizQuestionsOrderEnum.NON);

        break;
      }

      case QuizQuestionsOrderEnum.ASC: {
        const updatedOrder = base.sort((a, b) => {
          const aID = a.question!.originalId;
          const bID = b.question!.originalId;

          return aID - bID;
        });

        setQuizQuestions(updatedOrder);
        setQuizQuestionsOrder(QuizQuestionsOrderEnum.ASC);
        break;
      }

      case QuizQuestionsOrderEnum.DESC: {
        const updatedOrder = base.sort((a, b) => {
          const aID = a.question!.originalId;
          const bID = b.question!.originalId;

          return bID - aID;
        });

        setQuizQuestions(updatedOrder);
        setQuizQuestionsOrder(QuizQuestionsOrderEnum.DESC);
        break;
      }
    }
  };

  const QuizQuestionsOrderDegMap = {
    [QuizQuestionsOrderEnum.NON]: '0deg',
    [QuizQuestionsOrderEnum.ASC]: '-90deg',
    [QuizQuestionsOrderEnum.DESC]: '90deg',
  };

  useEffect(() => {
    setQuizQuestions(quiz?.quizQuestions);
  }, [quiz?.quizQuestions]);

  useEffect(() => {
    quizQuestionsRef.current = quizQuestions || [];
  }, [quizQuestions]);

  const isDirtQuizQuestionsOrder = isEqual(
    quizQuestions?.map((i) => i?.id),
    quiz?.quizQuestions?.map((i) => i?.id),
  );

  const hasUnsavedOrder = !isDirtQuizQuestionsOrder;

  // Warn on tab close / refresh
  useEffect(() => {
    if (!hasUnsavedOrder) return;

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener('beforeunload', handler);

    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedOrder]);

  // Block in-app navigation when there are unsaved order changes
  const blocker = useBlocker(hasUnsavedOrder);

  useEffect(() => {
    if (blocker.state !== 'blocked') return;

    const leave = window.confirm(
      'Leave site?\nChanges you made to the quiz order may not be saved.',
    );

    if (leave) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker]);

  const currentQuestionsIds =
    quiz?.quizQuestions?.reduce<number[]>((acc, { question }) => {
      if (question) {
        acc.push(question.id);
      }
      return acc;
    }, []) || [];

  const [update] = useMutation(UPDATE_QUIZ);

  const onChangeStatus = (value: QuizStatus) => {
    if (!quiz) return null;
    const updatePromise = update({
      variables: {
        input: {
          status: value,
          id: quiz.id,
          name: quiz.name,
          orderedQuestions: currentQuestionsIds.map((id, index) => ({
            questionId: id,
            order: index,
          })),
        },
      },
    });

    toast.promise(updatePromise, {
      success: 'Status changed',
      pending: 'Updating status...',
    });
  };

  const onAddQuestions = (ids: number[]) => {
    if (!quiz) return null;
    const array: OrderedQuestionsInput[] = currentQuestionsIds.map(
      (id, index) => ({
        questionId: id,
        order: index,
      }),
    );

    ids.forEach((id) => {
      if (!currentQuestionsIds.includes(id)) {
        array.push({
          questionId: id,
          order: array.length,
        });
      }
    });

    const updatePromise = update({
      variables: {
        input: {
          id: quiz.id,
          name: quiz.name,
          status: quiz.status,
          orderedQuestions: array,
        },
      },
    });

    toast.promise(updatePromise, {
      success: ids.length > 1 ? 'Questions added' : 'Question added',
      pending: ids.length > 1 ? 'Adding questions' : 'Adding a question',
    });
    updatePromise.then(() => onCloseQuestionBrowser());
  };

  const onChangeQuestion = (newId: number) => {
    if (!quiz || questionToChange == null) return;
    const updatePromise = update({
      variables: {
        input: {
          id: quiz.id,
          name: quiz.name,
          status: quiz.status,
          orderedQuestions: currentQuestionsIds.map((id, index) => {
            return {
              questionId: id === questionToChange ? newId : id,
              order: index,
            };
          }),
        },
      },
    });

    toast.promise(updatePromise, {
      success: 'Question replaced',
      pending: 'Replace the question...',
    });
    updatePromise.then(() => onCloseQuestionBrowser());
  };

  const onInsertQuestion = (newId: number) => {
    if (!quiz || insertQuestionAtIndex == null) return;

    const orderedQuestions: OrderedQuestionsInput[] = [];
    const nextQuestionIds = [...currentQuestionsIds];

    // Decrement the blank row count for the question at the insert index
    const targetQuestionId = currentQuestionsIds[insertQuestionAtIndex];
    if (targetQuestionId != null) {
      setPendingInserts((prev) => {
        const next = new Map(prev);
        const count = (next.get(targetQuestionId) ?? 1) - 1;
        if (count <= 0) next.delete(targetQuestionId);
        else next.set(targetQuestionId, count);
        return next;
      });
    }

    nextQuestionIds.splice(insertQuestionAtIndex, 0, newId);

    nextQuestionIds.forEach((id, index) => {
      orderedQuestions.push({
        questionId: id,
        order: index,
      });
    });

    const updatePromise = update({
      variables: {
        input: {
          id: quiz.id,
          name: quiz.name,
          status: quiz.status,
          orderedQuestions,
        },
      },
    });

    toast.promise(updatePromise, {
      success: 'Question inserted',
      pending: 'Inserting a question...',
    });
    updatePromise.then(() => onCloseQuestionBrowser());
  };

  const updateQuestionsOrder = () => {
    if (!quiz || (!isDirtQuizQuestionsOrder && !quizQuestions)) return;

    const updatePromise = update({
      variables: {
        input: {
          orderedQuestions: quizQuestions!.map(({ question }, index) => ({
            questionId: question!.id,
            order: index,
          })),
          id: quiz.id,
          name: quiz.name,
          status: quiz.status,
        },
      },
    });

    toast.promise(updatePromise, {
      success: 'Success',
      pending: 'Proccess',
    });
  };

  const onRemoveQuestions = (ids: number[]) => {
    if (!quiz) return null;

    const currentQuestions = quizQuestionsRef.current || [];
    const nextQuestions = currentQuestions.filter(
      ({ question }) => question && !ids.includes(question.id),
    );
    const updatePromise = update({
      variables: {
        input: {
          orderedQuestions: nextQuestions.map(({ question }, index) => ({
            questionId: question!.id,
            order: index,
          })),
          id: quiz.id,
          name: quiz.name,
          status: quiz.status,
        },
      },
    });

    if (ids.length !== 1) {
      toast.promise(updatePromise, {
        success: ids.length > 1 ? 'Questions removed' : 'Question removed',
        pending: ids.length > 1 ? 'Removing questions' : 'Removing a question',
      });

      updatePromise.then(() => {
        quizQuestionsRef.current = nextQuestions;
        setQuizQuestions(nextQuestions);
      });

      return updatePromise;
    }

    const removedQuestionIndex = currentQuestions.findIndex(
      ({ question }) => question?.id === ids[0],
    );
    const removedQuestion = currentQuestions[removedQuestionIndex];

    if (!removedQuestion?.question) {
      toast.promise(updatePromise, {
        success: 'Question removed',
        pending: 'Removing a question',
      });

      updatePromise.then(() => {
        quizQuestionsRef.current = nextQuestions;
        setQuizQuestions(nextQuestions);
      });

      return updatePromise;
    }

    const pendingToastId = toast.loading('Removing a question...');

    updatePromise
      .then(() => {
        toast.dismiss(pendingToastId);
        quizQuestionsRef.current = nextQuestions;
        setQuizQuestions(nextQuestions);

        toast(
          ({ closeToast }) => (
            <div className="ml-1 flex items-center gap-3">
              <span>{`Question ${removedQuestion.question!.originalId} removed`}</span>
              <button
                className="text-sm font-medium text-blue hover:underline"
                onClick={() => {
                  closeToast?.();

                  const currentQuestionsSnapshot = quizQuestionsRef.current || [];

                  if (
                    currentQuestionsSnapshot.some(
                      ({ question }) => question?.id === removedQuestion.question!.id,
                    )
                  ) {
                    return;
                  }

                  const restoredQuestions = [...currentQuestionsSnapshot];
                  restoredQuestions.splice(
                    Math.min(removedQuestionIndex, restoredQuestions.length),
                    0,
                    removedQuestion,
                  );

                  const restorePromise = update({
                    variables: {
                      input: {
                        orderedQuestions: restoredQuestions.map(
                          ({ question }, index) => ({
                            questionId: question!.id,
                            order: index,
                          }),
                        ),
                        id: quiz.id,
                        name: quiz.name,
                        status: quiz.status,
                      },
                    },
                  });

                  toast.promise(restorePromise, {
                    success: 'Question restored',
                    pending: 'Restoring question...',
                  });

                  restorePromise.then(() => {
                    quizQuestionsRef.current = restoredQuestions;
                    setQuizQuestions(restoredQuestions);
                  });
                }}
              >
                Undo
              </button>
            </div>
          ),
          {
            autoClose: 5000,
            closeOnClick: false,
          },
        );
      })
      .catch(() => {
        toast.dismiss(pendingToastId);
      });

    return updatePromise;
  };

  const onOpenQuestionBrowser = (id?: number) => {
    setInsertQuestionAtIndex(null);
    if (typeof id === 'number') setQuestionToChange(id);
    else setQuestionToChange(null);
    setQuestionsBrowser(true);
  };

  const onOpenInsertQuestionBrowser = (index: number) => {
    setQuestionToChange(null);
    setInsertQuestionAtIndex(index);
    setQuestionsBrowser(true);
  };

  const onCloseQuestionBrowser = () => {
    setInsertQuestionAtIndex(null);
    setQuestionToChange(null);
    setSelectedQuestion([]);
    setQuestionsBrowser(false);
    setQuestionsBrowser(false);
  };

  const onCloseEditQuiz = () => setQuizEditModal(false);
  const onUpdateQuizName = (slug: string) => {
    onCloseEditQuiz();
    navigate(`/quizzes-browser/${slug}`, { replace: true });
  };

  const onEditQuiz = () => {
    if (quiz?.id) {
      client.writeQuery({
        query: GET_QUIZ,
        variables: { input: { id: quiz.id } },
        data,
      });
      setQuizLookupId({ routeSlug: slug, id: quiz.id });
    }
    setQuizEditModal(true);
  };

  const onClickRandom = () => {
    if (!quiz) return null;

    const shuffledQuestionIds = shuffleArray(currentQuestionsIds);
    const questionById = new Map(
      (quizQuestions || []).map((item) => [item.question!.id, item]),
    );

    setQuizQuestions(
      shuffledQuestionIds.flatMap((id) => {
        const question = questionById.get(id);

        return question ? [question] : [];
      }),
    );
    setQuizQuestionsOrder(QuizQuestionsOrderEnum.NON);
  };

  const onCloseRemoveAllModal = () => setRemoveAllModal(false);

  const onRemoveAll = () => {
    if (!quiz) return null;

    const updatePromise = update({
      variables: {
        input: {
          id: quiz.id,
          name: quiz.name,
          status: QuizStatus.Draft,
          orderedQuestions: [],
        },
      },
    });

    updatePromise.then(() => onCloseRemoveAllModal());

    toast.promise(updatePromise, {
      success: 'All questions have been removed from the quiz',
      pending: 'Deleting all questions in quiz...',
    });
  };

  const onExport = useCallback(
    (params: IQuestionsExportSettingsFormData) => {
      if (!quiz?.id) return null;
      void createExportLog({
        quizId: quiz.id,
        type: UserActivityLogType.ExportPdf,
      });
      const url = createPdfExportUrl('quiz', {
        id: quiz.id.toString(),
        ...params,
      });
      window.open(url);
    },
    [createExportLog, quiz?.id],
  );

  const createdBy = quiz?.createdBy;
  const createdByText = createdBy?.name || '(Deleted user)';
  const hasExpandedQuestions =
    isExpandedQuestions || expandedQuestionIds.size > 0;
  const onToggleExpandedQuestions = () => {
    setExpandedQuestionIds(new Set());
    setIsExpandedQuestions(!hasExpandedQuestions);
  };

  const renderStatusLabel = (status: string) => (
    <span className="flex items-center gap-2">
      {status === QuizStatus.Active && (
        <span
          className="h-3 w-3 flex-none rounded-full shadow-[0_0_0_3px_rgba(67,169,95,0.18)]"
          style={{ backgroundColor: '#43A95F' }}
        />
      )}
      <span>{status}</span>
    </span>
  );

  const statusClassName =
    quiz?.status === QuizStatus.Active
      ? 'border border-black bg-white text-black'
      : 'border border-gray-300 bg-[#eef2f7] text-gray-800';

  const questionsToolbarClassName = cn(
    'relative z-20 flex flex-col gap-3 border-b border-gray-100 bg-white py-3 shadow-sm md:flex-row md:items-center md:justify-between',
    isQuizEditModal
      ? 'relative'
      : "sticky top-0 before:pointer-events-none before:absolute before:-top-12 before:left-0 before:right-0 before:h-12 before:bg-white before:content-['']",
  );

  return (
    <div>
      <Breadcrumbs
        elements={[
          { title: 'Quizzes Browser', href: '/quizzes-browser' },
          { title: quiz?.name || '' },
        ]}
      />
      <div
        className={
          'relative z-30 mt-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'
        }
      >
        <div>
          <h1>{quiz?.name || 'Quiz Information'}</h1>
          {quiz?.description && (
            <p className={'mt-1 text-sm text-black'}>{quiz.description}</p>
          )}
          <p className={'mt-1 text-sm text-gray'}>
            Created by{' '}
            {createdBy?.id ? (
              <Link
                to={`/users-browser/${createdBy.id}`}
                className={'text-blue hover:underline'}
              >
                {createdByText}
              </Link>
            ) : (
              createdByText
            )}
            {quiz?.createdAt && (
              <>
                {' on '}
                <LocalDate date={quiz.createdAt} format={"PPP 'at' p"} />
              </>
            )}
          </p>
        </div>

        <div className={'flex flex-wrap gap-2'}>
          <div className="rounded-full ring-1 ring-black/5 shadow-sm">
            <Select
              value={quiz?.status || QuizStatus.Draft}
              options={[QuizStatus.Draft, QuizStatus.Active]}
              onChange={(value) => onChangeStatus(value as QuizStatus)}
              className={cn(
                'min-w-[9.25rem] rounded-full px-4 py-2 text-sm font-medium',
                statusClassName,
              )}
              disabled={!canEdit}
              renderOption={renderStatusLabel}
              renderValue={renderStatusLabel}
            />
          </div>
          {canEdit && <Button onClick={onEditQuiz}>Edit quiz</Button>}
          <ActionsDropdown
            selected={quiz ? [quiz.id] : []}
            browser={NavBarEnum.QUIZZES_BROWSER}
          />
          {canExport && (
            <Popover
              triggerComponent={
                <button
                  className={
                    'box-border flex h-fit items-center justify-center rounded bg-light-blue p-2 text-sm font-medium text-black'
                  }
                >
                  Export
                </button>
              }
              contentProps={{ className: 'w-80' }}
            >
              <div className="grid gap-4">
                <div className="space-y-2">
                  <h4 className="font-medium leading-none">Export</h4>
                  <p className={'description'}>Set the quiz export settings</p>
                </div>
                <QuestionsExportSettingsForm onSubmit={onExport} />
              </div>
            </Popover>
          )}
          {(isAdmin || isTeacher) && <RemoveButton onClick={onOpenRemove} />}
        </div>
      </div>

      <section>
        <div className={questionsToolbarClassName}>
          <div className={'flex items-center gap-1'}>
            <h1 className={'text-gray-500'}>{quiz?.questionCount || 0}</h1>
            <h1>Questions</h1>
          </div>
          <div className={'flex flex-wrap items-end'}>
            {Boolean(quiz?.quizQuestions?.length) && (
              <Button
                onClick={() =>
                  copyTextToClipboard(
                    quizQuestions!
                      .map((q) => q.question?.originalId || q.question?.id)
                      .join('\n'),
                  )
                }
                className={'mx-2'}
              >
                Copy IDs
              </Button>
            )}

            <h1 className="ml-2">Order:</h1>

            <div className={'relative mx-1 flex flex-col items-center'}>
              {hasUnsavedOrder && (
                <span className="absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border border-red/20 bg-[#fff1f1] px-2 py-0.5 text-[10px] font-medium text-red">
                  Click Save to save changes
                </span>
              )}
              <Button
                onClick={updateQuestionsOrder}
                disabled={isDirtQuizQuestionsOrder}
              >
                Save
              </Button>
            </div>

            <Button
              onClick={() => {
                setQuizQuestions([...(quiz?.quizQuestions || [])]);
              }}
              className={'mx-1'}
              disabled={isDirtQuizQuestionsOrder}
            >
              Restore
            </Button>

            <Button onClick={quizQuestionToggle} className={'mx-1 flex gap-1'}>
              IDs
              {quizQuestionsOrder !== QuizQuestionsOrderEnum.NON && (
                <ArrowIcon
                  width={16}
                  height={16}
                  style={{
                    rotate: QuizQuestionsOrderDegMap[quizQuestionsOrder],
                  }}
                />
              )}
              {quizQuestionsOrder === QuizQuestionsOrderEnum.NON && (
                <CrossMarkIcon
                  width={16}
                  height={12}
                  style={{
                    rotate: QuizQuestionsOrderDegMap[quizQuestionsOrder],
                  }}
                />
              )}
            </Button>

            <Button
              onClick={onClickRandom}
              disabled={currentQuestionsIds.length < 2}
              white
              className={'mx-1'}
            >
              Random
            </Button>

            <Button
              onClick={onToggleExpandedQuestions}
              white
              className={'mx-1'}
              icon={
                hasExpandedQuestions ? (
                  <ChevronDoubleUpIcon className={'ml-1 h-4 w-4'} />
                ) : (
                  <ChevronDoubleDownIcon className={'ml-1 h-4 w-4'} />
                )
              }
            >
              {hasExpandedQuestions ? 'Collapse All' : 'Expand All'}
            </Button>

            <RemoveButton
              onClick={() => setRemoveAllModal(true)}
              disabled={!currentQuestionsIds.length}
            />
          </div>
        </div>

        {quiz?.quizQuestions && (
          <div className={'w-full'}>
            <Reorder.Group
              axis="y"
              values={quizQuestions!}
              onReorder={setQuizQuestions}
              className={'mb-1 flex flex-col overflow-x-auto'}
            >
              {quizQuestions?.map((quizQuestionData, i) => {
                const questionId = quizQuestionData.question!.id;
                const blankCount = pendingInserts.get(questionId) ?? 0;

                const addBlankRow = () => {
                  setPendingInserts((prev) => {
                    const next = new Map(prev);
                    next.set(questionId, (next.get(questionId) ?? 0) + 1);
                    return next;
                  });
                };

                const removeBlankRow = () => {
                  setPendingInserts((prev) => {
                    const next = new Map(prev);
                    const count = (next.get(questionId) ?? 1) - 1;
                    if (count <= 0) next.delete(questionId);
                    else next.set(questionId, count);
                    return next;
                  });
                };

                const openInsertModal = () => {
                  onOpenInsertQuestionBrowser(i);
                };

                return (
                  <Fragment key={quizQuestionData.id}>
                    {Array.from({ length: blankCount }).map((_, blankIdx) => (
                      <div
                        key={`blank-${questionId}-${blankIdx}`}
                        className={
                          'my-0.5 flex items-center border-2 border-dashed border-gray/30 bg-white px-2 py-3'
                        }
                      >
                        <div
                          className={
                            'flex min-w-0 flex-1 cursor-pointer items-center justify-center'
                          }
                          onClick={openInsertModal}
                        >
                          <span className={'text-base font-medium text-gray'}>
                            Insert Question
                          </span>
                        </div>

                        <div
                          className={
                            'flex w-[5.75rem] shrink-0 flex-col items-end gap-0.5 text-right'
                          }
                        >
                          <button
                            onClick={addBlankRow}
                            className={
                              'rounded px-1.5 py-0.5 text-xs font-medium leading-none text-blue hover:underline'
                            }
                          >
                            Insert Above
                          </button>
                          <button
                            onClick={openInsertModal}
                            className={
                              'rounded px-1.5 py-0.5 text-xs font-medium leading-none text-blue hover:underline'
                            }
                          >
                            Change
                          </button>
                          <button
                            onClick={removeBlankRow}
                            className={
                              'rounded px-1.5 py-0.5 text-xs font-medium leading-none text-[#F05252] hover:underline'
                            }
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}

                    <QuizQuestionItem
                      isExpanded={
                        isExpandedQuestions ||
                        expandedQuestionIds.has(questionId)
                      }
                      setPreviewQuestionId={setPreviewQuestionId}
                      quizQuestionData={quizQuestionData}
                      index={i}
                      onInsertQuestion={addBlankRow}
                      onChangeQuestion={() => onOpenQuestionBrowser(questionId)}
                      onRemoveQuestion={() =>
                        onRemoveQuestions([questionId])
                      }
                      onClickQuestion={() =>
                        setExpandedQuestionIds((current) => {
                          const next = new Set(current);

                          if (next.has(questionId)) {
                            next.delete(questionId);
                          } else {
                            next.add(questionId);
                          }

                          return next;
                        })
                      }
                    />
                  </Fragment>
                );
              })}
            </Reorder.Group>

            <QuizQuestion
              questionName={'+ Add question'}
              onClickQuestion={onOpenQuestionBrowser}
            />
          </div>
        )}
      </section>

      <AddQuestionsModal
        insertAtIndex={insertQuestionAtIndex}
        open={isQuestionsBrowser}
        onClose={onCloseQuestionBrowser}
        onAddQuestions={onAddQuestions}
        onInsertQuestion={onInsertQuestion}
        onChangeQuestion={onChangeQuestion}
        questionToChange={questionToChange}
        setSelectedQuestion={setSelectedQuestion}
        selectedQuestions={selectedQuestions}
      />

      <Modal
        title={'Edit quiz'}
        isOpen={isQuizEditModal}
        onClose={() => setQuizEditModal(false)}
      >
        <CreateQuizForm quiz={quiz} onCloseModal={onUpdateQuizName} />
      </Modal>

      <RemoveModal
        open={isRemoveAllModal}
        description={
          'This will permanently unlink all the questions of this quiz.'
        }
        onRemove={onRemoveAll}
        onClose={onCloseRemoveAllModal}
      />

      <RemoveModal
        open={removeModal}
        onRemove={() => onRemoveQuizzes(quiz ? [quiz.id] : [])}
        onClose={onCloseRemove}
      />

      {previewQuestionId && (
        <Modal
          title={'Question preview'}
          isOpen={true}
          onClose={() => setPreviewQuestionId(undefined)}
          fullScreen
        >
          <QuestionPreview questionId={previewQuestionId} />
        </Modal>
      )}
    </div>
  );
};
export default QuizInformation;
